import axios from "axios";
import { useEffect, useRef, useState } from "react";
import CompoundDetailPane from "@/components/reference/CompoundDetailPane";
import { useStandardSpectra } from "@/hooks/useReferenceCompounds";
import { standardListApi, standardSearchApi } from "@/services/api";
import type {
  StandardListItem,
  StandardListResponse,
} from "@/types/referenceCompound";

/**
 * 표준 라이브러리 브라우저 — 참조물질 DB와 **같은 2단 골격** (2026-08-20 통일).
 *
 * 원본(NIST MS Search) Names 화면(기획 v2 §1.3)은 좌측에 이름 목록이 항상
 * 떠 있고 우측에 선택 물질 상세가 붙는다. 타이핑은 목록 안의 **접두 위치
 * 이동**이다. 우리 화면은 두 단계에 걸쳐 그쪽으로 맞췄다:
 *
 * 1. (2026-08-20 1차) 검색 강제 폐지 — 목록을 항상 보여준다.
 * 2. (2026-08-20 2차, 이 파일) 표+모달 → **좌 목록 / 우 상세**. 참조물질 DB
 *    탭이 이미 쓰던 배치이고, 모달은 물질을 옮겨 가며 볼 때 흐름을 끊는다.
 *
 * 좌측 목록은 두 가지 방식으로 채운다 — **위치 이동**(사전순 keyset 페이지
 * 넘김, 기본)과 **포함 검색**(이름·분자식·SMILES 부분일치, 상위 50건).
 * 둘 다 같은 우측 패널로 이어진다.
 *
 * 정직성: 이 라이브러리는 기본적으로 이름·분자식·SMILES 만 갖는다 — MS
 * 스펙트럼이 없다는 사실을 헤더와 상세 패널이 함께 밝힌다. 다만 일부
 * asset(예: Wiley/NIST 스펙트럼 sidecar, T6-3 2026-08-27)은 스펙트럼도
 * 함께 갖는다 — 이때는 서버가 `has_spectra`/`license_notice`를 응답에
 * 실어 보내고, 화면은 그 값을 그대로 반영한다(하드코딩하지 않는다).
 * 라이선스 자료라 화면 조회만 허용하고 다운로드·복사 버튼은 두지 않는다.
 */

/**
 * 한 페이지에 받아 오는 건수.
 *
 * **100 → 25 (2026-09-06).** 목록이 페이지와 함께 흐르게 되면서 100건이면
 * 페이지가 6,400px 가 됐다 — 운영자 지적 "100개를 전체 펼쳐 보이니 스크롤이
 * 엄청 길다". 한 페이지가 한두 화면 안에 들어와야 '페이지'로 읽힌다.
 *
 * 페이지 수는 9,320 → 37,277 로 늘지만 **이 화면에서 페이징은 주 이동수단이
 * 아니다** — 이름을 치면 그 위치로 가고(사전순 위치 이동), 물질을 찾을 때는
 * 관련도 검색을 쓴다. 위치 줄이 절대 순번을 말하므로 몇 쪽인지도 문제가 안 된다.
 */
const PAGE_SIZE = 25;

/**
 * 포함 검색이 서버에서 받아 오는 최대 건수.
 *
 * **화면 문구는 이 상수 또는 실제 받은 행 수에서만 만든다.** 종전에는 UI 두
 * 곳에 `상위 50건` 이 글자로 박혀 있었고 실제 limit 은 세 곳(여기 미전달 →
 * `api.ts` 기본값, `useReferenceCompounds.ts`)에 흩어져 있어, 어느 한쪽만
 * 바뀌면 화면이 거짓말을 하게 되어 있었다 — `:600-603` 주석이 스펙트럼 줄에서
 * 이미 한 번 잡았던 실패의 재발이다(2026-08-27). 이제 요청도 이 상수로 보낸다.
 */
const SEARCH_LIMIT = 50;

/**
 * 화면에 고정되는 줄들의 세로 오프셋.
 *
 * 앱 상단 내비게이션이 `sticky` 로 64px 를 차지한다(실측). 목록 조작 줄은 그
 * 바로 아래에, 상세 패널은 한 칸 더 아래에 붙인다. `scroll-mt-*` 도 같은
 * 값을 써야 페이지를 목록 맨 위로 되돌렸을 때 첫 행이 고정 줄에 가리지 않는다.
 */
const STICKY_TOP = "top-16"; // 4rem = 64px = 내비 높이
const STICKY_TOP_DETAIL = "lg:top-20"; // 5rem — 상세 패널은 조작 줄과 겹치지 않게
const ROOT_SCROLL_MARGIN = "scroll-mt-20"; // 5rem — 페이지 넘김으로 되돌아올 때 내비를 비운다
/**
 * 목록 자신의 스크롤 여백.
 *
 * **브라우저가 목록으로 스크롤할 때**(Tab 으로 들어올 때) 쓰인다. 내비 64px +
 * 고정 조작 줄 43px = 107px 를 비워야 목록 첫 행이 그 뒤로 숨지 않는다.
 * 이걸 빼면 목록 위쪽이 고정 헤더 뒤로 들어가 **클릭도 안 된다**(적대적 검증
 * 실측: 가려진 행 0 → 2, elementFromPoint 가 내비를 돌려줬다).
 */
const LIST_SCROLL_MARGIN = "scroll-mt-28"; // 7rem = 112px — 내비 + 조작 줄

/** listbox 의 `aria-activedescendant` 가 가리킬 option 의 DOM id. */
const optionDomId = (rowid: number) => `std-lib-option-${rowid}`;

type ListMode = "jump" | "contains";

interface PageKey {
  /** 사용자가 타이핑한 위치 이동 접두 (커서와 다른 파라미터). */
  from_name: string;
  /** 서버가 발급한 다음 페이지 커서 — 이름 길이 제약과 무관하다. */
  after_name: string;
  after_rowid: number;
}

interface StandardLibraryBrowserProps {
  /** 위에 asset 요약 카드가 이미 있으면 헤더의 이름·버전·건수를 생략한다. */
  showAssetLabel?: boolean;
}

/**
 * 검색(포함 검색) 응답이 함께 싣는 자산 메타 (T6-3 후속, 2026-08-27) — 목록
 * 응답의 `has_spectra`/`license_notice`/`asset_name`/`asset_version` 과 같은
 * 의미. 검색 모드는 목록 응답(`data`)이 없을 수 있어 이 메타를 따로 둔다.
 */
interface SearchMeta {
  has_spectra: boolean;
  license_notice: string | null;
  asset_name: string | null;
  asset_version: string | null;
}

export default function StandardLibraryBrowser({
  showAssetLabel = true,
}: StandardLibraryBrowserProps) {
  const [mode, setMode] = useState<ListMode>("jump");
  const [input, setInput] = useState("");
  const [pageKey, setPageKey] = useState<PageKey>({
    from_name: "",
    after_name: "",
    after_rowid: 0,
  });
  // 이전 페이지로 돌아가기 위한 키 스택 (keyset 은 역방향이 없다)
  const [prevStack, setPrevStack] = useState<PageKey[]>([]);
  const [data, setData] = useState<StandardListResponse | null>(null);
  // `data` 를 만들어 낸 from_name. 접두 안내는 **이 값만** 인용한다 —
  // committedInput 은 ref 라 응답보다 먼저 앞서 나가고, 그러면 화면이 새
  // 접두어를 인용하면서 이전 접두어의 건수를 말한다(요청이 실패하면 그 상태가
  // 영구히 남는다). 적대적 검증에서 4개 렌즈가 각각 잡았다(2026-09-06).
  const [dataPrefix, setDataPrefix] = useState("");
  const [searchItems, setSearchItems] = useState<StandardListItem[] | null>(
    null,
  );
  const [searchTruncated, setSearchTruncated] = useState(false);
  // 잘라내기 전 진짜 일치 건수. null = 서버가 세지 않았다(구 서버) — 그때는
  // 종전처럼 "더 있습니다"까지만 말하고 숫자를 지어내지 않는다.
  const [searchTotal, setSearchTotal] = useState<number | null>(null);
  const [searchMeta, setSearchMeta] = useState<SearchMeta | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedRowid, setSelectedRowid] = useState<number | null>(null);
  // 복수 스펙트럼을 가진 물질에서 몇 번째를 보고 있는지 (T6-3, 2026-08-27).
  const [spectrumIndex, setSpectrumIndex] = useState(0);
  // spectrumIndex 를 언제 0 으로 되돌릴지 판단하는 데만 쓰는 이전 값.
  const [prevSpectraRowid, setPrevSpectraRowid] = useState<number | null>(null);
  const requestSeq = useRef(0);
  const listRef = useRef<HTMLDivElement | null>(null);
  const rootRef = useRef<HTMLDivElement | null>(null);

  /**
   * **페이지를 넘길 때만** 이 화면의 맨 위로 되돌린다.
   *
   * 종전에는 목록이 자체 스크롤 상자였고 scrollTop 이 그대로 남아(실측
   * 4357 → 4357) 새 페이지의 앞 88행을 건너뛴 자리에 착지했다. 이제 목록은
   * 페이지와 함께 흐르므로(자르는 면을 둘에서 하나로 줄였다) **페이지**를
   * 되돌려야 같은 뜻이 된다.
   *
   * 두 가지가 중요하다.
   *
   * 1. **되돌리는 대상은 목록이 아니라 이 화면 전체(rootRef)** 다. 목록 위로
   *    맞추면 그 위에 있는 **검색창이 화면 밖으로 밀려난다** — 운영자가
   *    "입력 중인데 검색창이 사라진다"고 제기한 그것이다(실측: 디바운스 후
   *    scrollY 21 → 464, 입력창 y=15 로 내비 뒤에 숨었다).
   * 2. **타이핑에서는 부르지 않는다.** 사람이 글자를 치는 도중에 페이지가
   *    움직이면 안 된다. 페이지 넘김(goNext/goPrev)에서만 부른다.
   *
   * `scrollTo()` 가 아니라 `scrollIntoView` 인 이유 — jsdom 에 scrollTo 는
   * 없고 scrollIntoView 는 무해한 스텁이 있다(setup.ts).
   */
  const scrollToBrowserTop = () => {
    rootRef.current?.scrollIntoView({ block: "start" });
  };

  // 위치 이동/포함 검색: 타이핑(debounce). committedInput 과 비교해 **실제로
  // 바뀐 입력에만** 반응한다 — 마운트 직후 빈 값으로 한 번 더 요청하거나,
  // 400ms 안에 누른 '다음 페이지'를 되돌리던 문제 방지 (교차 리뷰).
  const committedInput = useRef("");
  const committedMode = useRef<ListMode>("jump");
  useEffect(() => {
    const next = input.trim();
    if (next === committedInput.current && mode === committedMode.current) {
      return;
    }
    const timer = setTimeout(() => {
      committedInput.current = next;
      committedMode.current = mode;
      setPrevStack([]);
      setSelectedRowid(null);
      // **여기서 스크롤하지 않는다.** 타이핑은 페이지 넘김이 아니다 —
      // 디바운스가 끝나는 순간 페이지가 움직이면 사람이 보고 있던 검색창이
      // 사라진다(위 scrollToBrowserTop 주석의 실측).
      // 검색어·모드가 바뀌면 이전 검색의 결과와 "절단됐다"는 사실을 **함께**
      // 버린다. 종전에는 건수만 남아, 포함 검색 → 위치 이동 → 포함 검색을
      // 왕복하면 배너가 "32,463건 중 상위 0건만 표시했습니다"라고 말했다.
      // catch 분기가 이미 지키는 "검색어와 결과는 한 쌍" 불변을 커밋 경로에도
      // 적용하는 것이다 (적대적 검증 확증, 2026-09-06).
      setSearchItems(null);
      setSearchTruncated(false);
      setSearchTotal(null);
      setSearchMeta(null);
      if (mode === "jump") {
        setPageKey({ from_name: next, after_name: "", after_rowid: 0 });
      } else {
        setPageKey({ from_name: "", after_name: "", after_rowid: 0 });
      }
    }, 400);
    return () => clearTimeout(timer);
  }, [input, mode]);

  // 목록 조회 — 위치 이동(keyset) 또는 포함 검색
  useEffect(() => {
    const seq = ++requestSeq.current;
    const contains = committedMode.current === "contains";
    const query = committedInput.current;
    if (contains && query.length < 2) {
      setSearchItems([]);
      setSearchTruncated(false);
      setSearchTotal(null);
      setIsLoading(false);
      setError(null);
      return;
    }
    setIsLoading(true);
    setError(null);
    const request = contains
      ? standardSearchApi.search(query, SEARCH_LIMIT).then((resp) => {
          if (requestSeq.current !== seq) return;
          // 포함 검색 응답이 rowid 를 주면(T6-3 후속, 2026-08-27) 위치 이동
          // 목록과 **같은 실제 DB rowid** 라 스펙트럼 조회에 그대로 쓸 수
          // 있다. rowid 가 없거나 null 인 구 응답은 목록 선택 키로만 쓰는
          // 음수 합성 인덱스로 대신한다(keyset rowid 와 섞이지 않게 항상
          // 음수) — 아래 spectraEligible 이 `rowid >= 0` 으로 실제/합성을
          // 가른다.
          setSearchItems(
            resp.data.items.map((it, i) => ({
              rowid: it.rowid ?? -(i + 1),
              name: it.name,
              formula: it.formula ?? null,
              smiles: it.smiles ?? null,
              // 구 sidecar 응답에는 두 컬럼이 없다 — undefined 를 그대로 넘기면
              // 상세 패널이 출처 줄을 두지 않는다 (하위호환).
              smiles_source: it.smiles_source ?? null,
              stereo_state: it.stereo_state ?? null,
              spectrum_count: it.spectrum_count ?? null,
            })),
          );
          setSearchTruncated(resp.data.truncated);
          setSearchTotal(resp.data.total_matches ?? null);
          // 검색 응답도 목록 응답과 같은 asset 메타를 싣는다(T6-3 후속,
          // 2026-08-27) — 검색 모드에서는 이 메타로 스펙트럼 조회 가능
          // 여부를 판단한다(아래 activeHasSpectra).
          setSearchMeta({
            has_spectra: resp.data.has_spectra,
            license_notice: resp.data.license_notice,
            asset_name: resp.data.asset_name,
            asset_version: resp.data.asset_version,
          });
        })
      : standardListApi
          .list({
            from_name: pageKey.from_name || undefined,
            after_name: pageKey.after_name || undefined,
            after_rowid: pageKey.after_rowid || undefined,
            limit: PAGE_SIZE,
          })
          .then((resp) => {
            if (requestSeq.current !== seq) return;
            setData(resp.data);
            // 효과가 `[pageKey]` 에 걸려 있어 이 클로저의 from_name 이 곧
            // 이 응답을 만들어 낸 접두어다 — 둘을 함께 갱신한다.
            setDataPrefix(pageKey.from_name);
          });

    request
      .catch((err: { response?: { status?: number } }) => {
        if (requestSeq.current !== seq) return;
        const status = err.response?.status;
        // 실패해도 **이전 목록·페이지 위치는 유지**한다 — 한 번의 오류로
        // 보던 화면이 통째로 사라지면 되돌아갈 방법이 없다 (교차 리뷰).
        if (status === 404) {
          setData(null);
          setSearchItems(null);
          setError(
            "활성 표준 라이브러리가 없습니다 — 관리자가 등록하면 표시됩니다.",
          );
        } else if (status === 503) {
          setError("검색 인덱스를 읽을 수 없습니다 — 관리자에게 문의하세요.");
        } else {
          setError("목록을 불러오지 못했습니다.");
        }
        // 포함 검색은 **검색어와 결과가 한 쌍**이다. 실패한 검색어 아래에 이전
        // 검색어의 결과를 남기면 화면이 없는 사실을 말한다(상세 패널까지 그
        // 물질을 가리킨다) — 목록을 비운다. 위치 이동 목록은 페이지 위치를
        // 잃으면 되돌아갈 방법이 없으므로 그대로 둔다 (2026-08-20 교차 리뷰).
        if (contains) {
          setSearchItems(null);
          setSearchTruncated(false);
          setSearchTotal(null);
          setSearchMeta(null);
        }
      })
      .finally(() => {
        if (requestSeq.current === seq) setIsLoading(false);
      });
  }, [pageKey]);

  const isContains = committedMode.current === "contains";
  const items: StandardListItem[] = isContains
    ? (searchItems ?? [])
    : (data?.items ?? []);
  const selected =
    items.find((i) => i.rowid === selectedRowid) ?? items[0] ?? null;

  // 검색 모드는 목록 응답(data)이 없을 수 있다 — 검색 응답 자체가 준
  // has_spectra/license_notice/asset_name 을 근거로 쓴다(T6-3 후속,
  // 2026-08-27, 백엔드가 검색 응답에도 같은 필드를 싣는다). 구 검색
  // 응답(필드 없음)은 has_spectra:false 로 접혀 종전처럼 동작한다.
  const activeHasSpectra = isContains
    ? (searchMeta?.has_spectra ?? false)
    : (data?.has_spectra ?? false);
  const activeLicenseNotice = isContains
    ? (searchMeta?.license_notice ?? null)
    : (data?.license_notice ?? null);
  const activeAssetName = isContains
    ? (searchMeta?.asset_name ?? null)
    : (data?.asset_name ?? null);

  // 스펙트럼 (T6-3, 2026-08-27 + 후속) — has_spectra 인 asset 에서만, 그리고
  // **실제 DB rowid** 를 가진 항목에서만 가져온다. 위치 이동(keyset) 목록은
  // 항상 실제 rowid 다. 포함 검색은 서버가 rowid 를 주면(위 매핑 참고) 그
  // 값이 실제 rowid 라 마찬가지로 조회할 수 있고, rowid 를 안 주는 구
  // 응답은 음수 합성 인덱스로 대신하므로 `rowid >= 0` 체크로 자동 제외된다
  // — 모드가 아니라 rowid 의 성격으로 가른다.
  const spectraEligible =
    activeHasSpectra &&
    !!selected &&
    selected.rowid >= 0 &&
    selected.spectrum_count !== 0;
  const spectraRowid = spectraEligible && selected ? selected.rowid : null;
  const {
    data: spectraResp,
    isLoading: isSpectraLoading,
    isError: isSpectraError,
    error: spectraError,
    refetch: refetchSpectra,
  } = useStandardSpectra(spectraRowid, spectraRowid != null);

  // 선택(또는 조회 대상)이 바뀌면 이전 물질의 복수 스펙트럼 중 몇 번째를
  // 보던 것인지를 지운다 — 안 그러면 물질을 옮겼는데 "2/3" 같은 낡은
  // 인덱스가 남는다. useEffect 대신 **렌더 중 상태 조정** 패턴을 쓴다 — 이
  // 값 변화를 감지하는 게 목적이라 useEffect 본문이 spectraRowid 자체를
  // 안 쓰고, exhaustive-deps 관점에선 그게 불필요한 의존성으로 보인다.
  if (spectraRowid !== prevSpectraRowid) {
    setPrevSpectraRowid(spectraRowid);
    setSpectrumIndex(0);
  }

  const spectra = spectraResp?.spectra ?? [];
  const currentSpectrum =
    spectra.length > 0
      ? spectra[Math.min(spectrumIndex, spectra.length - 1)]
      : null;
  const isSpectra404 =
    isSpectraError &&
    axios.isAxiosError(spectraError) &&
    spectraError.response?.status === 404;
  const spectrumSourceLabel = currentSpectrum
    ? activeAssetName
      ? `${currentSpectrum.library} · ${activeAssetName}`
      : currentSpectrum.library
    : null;
  // 선택 변경 시 구조-only → 스펙트럼+구조 사이를 오가며 구조도(RDKit
  // canvas)가 재마운트되는 것을 막는 신호 (적대적 리뷰 확증, 2026-08-27) —
  // 로딩 중이거나 이미 스펙트럼이 있으면 상세 패널이 최종 2단 레이아웃을
  // 미리 잡아 둔다("불러오는 중…" 자리를 스펙트럼 자리와 같은 크기로).
  const spectrumPending =
    spectraEligible && (isSpectraLoading || spectra.length > 0);

  /**
   * listbox 키보드 순회 (WAI-ARIA listbox 패턴).
   *
   * **listbox 요소에만** 붙인다 — document 단위 핸들러로 만들면 위쪽 검색
   * 입력의 타이핑(이 화면에서 타이핑은 '사전순 위치로 이동'이다)을 가로챈다.
   * 목록 안에서 처리한 키만 preventDefault 하고, 나머지(Tab·문자키)는 그대로
   * 흘려 보낸다.
   */
  const onListKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (items.length === 0) return;
    const current = selected
      ? items.findIndex((i) => i.rowid === selected.rowid)
      : -1;
    const last = items.length - 1;
    let next: number;
    switch (event.key) {
      case "ArrowDown":
        next = Math.min(current + 1, last);
        break;
      case "ArrowUp":
        next = Math.max(current - 1, 0);
        break;
      case "Home":
        next = 0;
        break;
      case "End":
        next = last;
        break;
      case "PageDown":
        next = Math.min(current + 10, last);
        break;
      case "PageUp":
        next = Math.max(current - 10, 0);
        break;
      default:
        return;
    }
    event.preventDefault();
    const target = items[next];
    if (!target) return;
    setSelectedRowid(target.rowid);
    // 화면 밖으로 나간 항목을 따라간다. `block: "nearest"` 라 이미 보이는
    // 항목에서는 아무 것도 움직이지 않는다.
    listRef.current
      ?.querySelector(`[id="${optionDomId(target.rowid)}"]`)
      ?.scrollIntoView({ block: "nearest" });
  };

  const goNext = () => {
    if (!data?.next_cursor) return;
    const cursor = data.next_cursor;
    setPrevStack((stack) => [...stack, pageKey]);
    setSelectedRowid(null);
    scrollToBrowserTop();
    setPageKey({
      from_name: pageKey.from_name,
      after_name: cursor.name,
      after_rowid: cursor.rowid,
    });
  };

  const goPrev = () => {
    // updater 안에서 다른 setState 를 부르지 않는다 (StrictMode 이중 호출 시
    // 스택이 두 번 pop 될 수 있다) — 밖에서 계산해 둘 다 갱신한다.
    if (prevStack.length === 0) return;
    const key = prevStack[prevStack.length - 1];
    setPrevStack((stack) => stack.slice(0, -1));
    setSelectedRowid(null);
    scrollToBrowserTop();
    setPageKey(key);
  };

  return (
    // `scroll-mt-20` — 페이지 넘김으로 되돌아올 때 내비(64px) 뒤에 가리지 않게.
    <div ref={rootRef} className={ROOT_SCROLL_MARGIN}>
      <div className="mb-3">
        <h3 className="text-sm font-semibold text-gray-900">
          라이브러리 목록
          {/* 총 건수는 여기가 아니라 목록 바로 위 '위치 줄'이 말한다 — 헤더
              분기는 호출부 두 곳이 모두 showAssetLabel={false} 라 한 번도
              렌더되지 않는 죽은 코드였고(2026-09-06 실측), 총 건수는 페이지를
              넘기는 사람에게 **항상** 보여야 하는 값이다. */}
          {data && showAssetLabel && (
            <span className="ml-2 text-xs font-normal text-gray-500">
              {data.asset_name}
              {data.asset_version ? ` v${data.asset_version}` : ""}
            </span>
          )}
        </h3>
        <p className="text-xs text-gray-500 mt-0.5">
          {isContains ? (
            <>
              이름·분자식·SMILES <strong>부분 일치</strong> 검색 — 관련도
              순(이름 정확 일치 → SMILES 일치 → 분자식 일치 → 이름 접두 → 짧은
              이름)으로 최대 {SEARCH_LIMIT}건 돌려줍니다.
            </>
          ) : (
            <>
              이름 사전순 상시 목록 — 입력하면 그 위치로 이동합니다 (원본 Names
              방식).
            </>
          )}{" "}
          {/* 하드코딩하지 않는다 — 서버가 이 asset 의 스펙트럼 보유 여부를
              응답에 싣는다(`has_spectra`). 언젠가 스펙트럼 있는 asset 이
              들어와도 화면이 거짓말하지 않도록. 검색 모드는 검색 응답
              자체의 값을(activeHasSpectra), 위치 이동 모드는 목록 응답의
              값을 쓴다 (T6-3 후속, 2026-08-27). */}
          {activeHasSpectra ? (
            <>이 라이브러리는 MS 스펙트럼을 포함합니다.</>
          ) : (
            <>
              이 라이브러리는{" "}
              <strong>이름·분자식·SMILES 만 있고 MS 스펙트럼은 없습니다</strong>{" "}
              — 스펙트럼 비교는 <strong>표준물질 DB</strong> 탭에서 합니다.
            </>
          )}
        </p>
        {/* 라이선스 고지 — 서버가 보낸 문구를 그대로 보여준다(하드코딩하지
            않는다). has_spectra 일 때만 온다. 경고가 아니라 사실 고지라
            상태색(amber/red)을 쓰지 않는다. */}
        {activeHasSpectra && activeLicenseNotice && (
          <div className="mt-1.5 rounded border border-gray-200 bg-gray-50 px-2.5 py-1.5 text-[11px] text-gray-600">
            {activeLicenseNotice}
          </div>
        )}
        {data && data.unnamed_excluded > 0 && (
          <p className="text-xs text-amber-700 mt-0.5">
            ⚠ 이름이 비어 있는 레코드 {data.unnamed_excluded}건은 목록에 나오지
            않습니다 (구 색인 파일 — 관리자가 재색인하면 해소).
          </p>
        )}
      </div>

      {/* 검색 줄 — 원본의 [검색어][지우기] + 방식 전환 */}
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <label className="sr-only" htmlFor="std-lib-input">
          {mode === "jump" ? "라이브러리 위치 이동" : "라이브러리 포함 검색"}
        </label>
        <input
          id="std-lib-input"
          type="text"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder={
            mode === "jump"
              ? "위치 이동 — 이름 접두 입력 (예: hex → Hexane…)"
              : "포함 검색 — 이름·분자식·SMILES (2자 이상)"
          }
          className="w-96 max-w-full rounded border border-gray-300 px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
        <button
          type="button"
          onClick={() => setInput("")}
          className="rounded border border-gray-300 bg-white px-2 py-1 text-xs hover:bg-gray-50"
        >
          지우기
        </button>
        <div className="flex overflow-hidden rounded border border-gray-300">
          {(
            [
              // "위치 이동" 만으로는 무엇을 기준으로 이동하는지가 안 보인다 —
              // 이 모드는 **이름 사전순 목록 안의 위치**로 가는 것이지 검색이
              // 아니다(접두가 0건이어도 그 자리부터 나열된다).
              ["jump", "사전순 위치로 이동"],
              ["contains", "포함 검색"],
            ] as [ListMode, string][]
          ).map(([key, label]) => (
            <button
              key={key}
              type="button"
              onClick={() => setMode(key)}
              className={`px-2 py-1 text-xs ${
                mode === key
                  ? "bg-indigo-600 text-white"
                  : "bg-white text-gray-700 hover:bg-gray-50"
              }`}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      {/* 절단 고지 — 건수는 **실제로 화면에 있는 행 수**로 말한다(상수를 글자로
          박지 않는다: `SEARCH_LIMIT` 주석 참조).
          종전 문구의 "검색어를 더 구체적으로 적으세요" 는 이 데이터에서
          **틀린 조언**이었다 — 931,909종 라이브러리에서 더 구체적으로 적으면
          (예: `2,4-dinitrotoluene`) 0건이 되기 십상이다. 사용자가 할 수 있는
          일이 아니라 서버가 무엇을 했는지를 말한다. */}
      {isContains && searchTruncated && (
        <div className="mb-3 rounded border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
          {/* 서버가 진짜 총 건수를 주면 그것을 말한다 — "더 있을 수 있습니다"
              로는 검색어가 너무 넓은지 좁은지 판단할 수 없다. 안 주는 서버에서는
              숫자를 지어내지 않고 종전대로 말한다. */}
          {searchTotal != null ? (
            <>
              ⚠ 조건에 맞는 물질 {searchTotal.toLocaleString("ko-KR")}건 중 상위{" "}
              {items.length}건만 표시했습니다.
            </>
          ) : (
            <>
              ⚠ 상위 {items.length}건만 표시했습니다 — 조건에 맞는 물질이 더
              있습니다.
            </>
          )}
        </div>
      )}

      {/* 접두 경계 — 위치 이동은 **검색이 아니다.** 접두 일치가 0건이어도
          목록은 사전순 다음 위치부터 나오고, 화면은 그 첫 행을 자동 선택해
          구조도까지 그린다(실측: `xylene` → `Xylitol` 이 선택된 채 그려졌다).
          그러면 사람이 그것을 "찾았다"로 읽을 수 있다. 서버가 센 접두 건수를
          그대로 말해 그 오독을 막는다. 0 과 양수는 다른 사실이라 다르게 말한다. */}
      {!isContains && dataPrefix && data?.prefix_count != null && (
        <div
          className={`mb-3 rounded border px-3 py-2 text-xs ${
            data.prefix_count === 0
              ? "border-amber-200 bg-amber-50 text-amber-800"
              : "border-gray-200 bg-gray-50 text-gray-600"
          }`}
        >
          {data.prefix_count === 0 ? (
            <>
              ⚠ ‘{dataPrefix}’ 로 시작하는 이름이 <strong>없습니다</strong> —
              아래는 검색 결과가 아니라 사전순으로 그 다음 위치입니다.
            </>
          ) : (
            <>
              ‘{dataPrefix}’ 로 시작하는 이름{" "}
              {data.prefix_count.toLocaleString("ko-KR")}종 — 아래는 그 위치부터
              사전순입니다.
            </>
          )}
        </div>
      )}

      {error && (
        <div className="mb-3 flex flex-wrap items-center gap-2 rounded border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
          <span>{error}</span>
          <button
            type="button"
            onClick={() => setPageKey((k) => ({ ...k }))}
            className="ml-auto rounded border border-amber-300 bg-white px-2 py-0.5 text-xs hover:bg-amber-100"
          >
            다시 시도
          </button>
        </div>
      )}

      <div className="grid grid-cols-12 gap-4">
        {/* 좌: 이름 목록 (참조물질 DB 탭과 같은 리스트박스).
            폭은 **자산이 무엇을 가졌는지에 따라** 정한다(하드코딩하지 않는다):
            스펙트럼이 없는 자산에서는 상세 패널이 320×220 구조도 하나만 그려
            (CompoundDetailPane 의 no-spectra 분기) 넓은 우측이 비므로, 실제로
            읽어야 하는 이름 목록에 폭을 준다. 스펙트럼이 있으면 그 반대다. */}
        <div
          className={`col-span-12 ${
            activeHasSpectra ? "lg:col-span-4" : "lg:col-span-6"
          }`}
        >
          {/* 위치 줄 + 페이지 이동 — 목록 **위**에 둔다.
              종전에는 목록 아래에 있어 1600×950 실측에서 버튼이 y=1076,
              즉 뷰포트보다 126px 아래였다. 첫 화면만 보는 사람에게는 "다음"이
              있다는 사실 자체가 보이지 않았고, 목록 안쪽은 9.0화면이라
              스크롤로 닿기도 어려웠다. 위로 올리면 목록의 첫 행과 같은 시야에
              들어온다. */}
          {!isContains && (
            <div
              className={`sticky ${STICKY_TOP} z-10 mb-2 flex flex-wrap items-center justify-between gap-2 border-b border-gray-200 bg-white py-2`}
            >
              {/* 위치는 **서버가 센 절대 순번**으로만 말한다. 페이지 번호를
                  start/PAGE_SIZE 로 지어내지 않는다 — 위치 이동 뒤에는 페이지
                  경계가 100 의 배수와 어긋나 "6,090쪽"이 거짓이 된다.
                  서버가 위치를 주지 않으면(구 서버) 아는 것까지만 말한다. */}
              <span className="text-[11px] text-gray-500">
                {data?.total_names != null && data.start_position != null ? (
                  <>
                    {data.total_names.toLocaleString("ko-KR")}종 중{" "}
                    {data.start_position.toLocaleString("ko-KR")}–
                    {(data.start_position + items.length - 1).toLocaleString(
                      "ko-KR",
                    )}
                    번째
                  </>
                ) : (
                  <>
                    {data?.record_count != null && (
                      <>전체 {data.record_count.toLocaleString("ko-KR")}종 · </>
                    )}
                    이 페이지 {items.length}건
                  </>
                )}
              </span>
              <span className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={goPrev}
                  disabled={prevStack.length === 0 || isLoading}
                  className="rounded border border-gray-300 bg-white px-2 py-1 text-xs hover:bg-gray-50 disabled:opacity-40"
                >
                  ← 이전
                </button>
                <button
                  type="button"
                  onClick={goNext}
                  disabled={!data?.next_cursor || isLoading}
                  className="rounded border border-gray-300 bg-white px-2 py-1 text-xs hover:bg-gray-50 disabled:opacity-40"
                >
                  다음 →
                </button>
              </span>
            </div>
          )}

          {isLoading && items.length === 0 ? (
            <div className="animate-pulse space-y-2">
              {[1, 2, 3, 4, 5, 6].map((i) => (
                <div key={i} className="h-7 rounded bg-gray-200" />
              ))}
            </div>
          ) : items.length === 0 ? (
            <p className="rounded border border-gray-200 px-3 py-6 text-center text-sm text-gray-500">
              {/* "0건이었다"와 "못 물어봤다"는 다른 사실이다. 조회가 실패하면
                  catch 분기가 목록을 null 로 비우는데, 종전에는 그 상태에서도
                  "검색 결과가 없습니다"라고 단정해 오류 배너와 나란히 모순된
                  두 문장이 떴다 (적대적 검증 확증, 2026-09-06). */}
              {isContains
                ? committedInput.current.length < 2
                  ? "2자 이상 입력하세요."
                  : searchItems === null
                    ? "검색을 마치지 못했습니다 — 일치 건수를 알 수 없습니다."
                    : "검색 결과가 없습니다."
                : data === null
                  ? "목록을 불러오지 못했습니다."
                  : "이 위치 이후에 항목이 없습니다."}
            </p>
          ) : (
            <div
              ref={listRef}
              role="listbox"
              aria-label="표준 라이브러리 목록"
              // 목록 전체가 **탭 스톱 하나**다. 종전에는 행마다 버튼이라
              // 탭 스톱이 100개였고, 그러면서도 ↓ 키로는 한 칸도 못 움직였다
              // (role="listbox" 인데 키보드 계약이 없었다). 저장소에 선례가
              // 없어 여기서 WAI-ARIA listbox 패턴을 처음 세운다.
              tabIndex={0}
              aria-activedescendant={
                selected ? optionDomId(selected.rowid) : undefined
              }
              onKeyDown={onListKeyDown}
              // **자체 스크롤 상자를 두지 않는다.** 종전에는 목록이 34rem
              // 상자였고 그 상자의 아래 모서리가 뷰포트보다 152px 아래에
              // 있었다(1280×800 에선 302px) — 자르는 면이 상자와 창 두 개라
              // 어디가 끝인지 알 수 없었고, 상자 모서리에 걸친 행은 49px 중
              // 42px만 보이게 반쯤 잘렸다. 이제 목록은 페이지와 함께 흐르고
              // 자르는 면은 창 하나다(2026-09-06 실측).
              className={`${LIST_SCROLL_MARGIN} rounded border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-inset focus:ring-blue-500 ${
                isLoading ? "opacity-60" : ""
              }`}
            >
              {items.map((item) => {
                const isSelected = selected?.rowid === item.rowid;
                return (
                  <button
                    key={item.rowid}
                    id={optionDomId(item.rowid)}
                    type="button"
                    role="option"
                    aria-selected={isSelected}
                    // 활성 항목은 aria-activedescendant 가 가리킨다 — option
                    // 자체는 탭 순서에서 빼되 클릭은 그대로 살린다.
                    tabIndex={-1}
                    // 클릭해도 포커스는 listbox 에 남긴다. 종전에는 포커스가
                    // option 버튼으로 옮겨 가 aria-activedescendant 를 든
                    // 요소가 포커스를 잃었고, 그 뒤 방향키로 선택이 움직여도
                    // 보조기술에는 아무것도 알려지지 않았다(화면은 8행, 낭독은
                    // 3행). mousedown 기본동작을 막아 포커스 이동 자체를
                    // 없애고, 브라우저 차이를 덮으려 focus() 도 부른다.
                    //
                    // `preventScroll` 이 핵심이다 — 그냥 focus() 하면 브라우저가
                    // 목록을 시야로 끌어오느라 **페이지를 576px 움직이고**
                    // 방금 고른 행을 고정 헤더 뒤로 밀어 넣는다(적대적 검증
                    // 실측: 클릭한 행이 y=675 → 99, 앞 2행은 클릭조차 안 됐다).
                    // 클릭한 행은 이미 눈앞에 있으므로 끌어올 이유가 없다.
                    //
                    // 행 자신에게도 스크롤 여백을 준다 — 방향키(↑/Home)가
                    // 부르는 `option.scrollIntoView` 는 **행**을 기준으로
                    // 움직이므로 목록의 여백으로는 못 막는다. 없으면 위로
                    // 올라갈 때 선택된 행이 내비+고정 조작 줄 뒤에 완전히
                    // 가려진다(적대적 검증 실측: 78% 가림).
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => {
                      setSelectedRowid(item.rowid);
                      listRef.current?.focus({ preventScroll: true });
                    }}
                    className={`w-full ${LIST_SCROLL_MARGIN} border-b border-gray-100 px-3 py-1.5 text-left last:border-b-0 ${
                      isSelected
                        ? "bg-indigo-600 text-white"
                        : "hover:bg-gray-50"
                    }`}
                  >
                    <span className="flex items-center gap-1.5">
                      {/* 화합물 이름은 잘리면 **다른 물질과 구별할 수 없게
                          된다** — 40자에서 라이브러리의 15.2%가 분자식이 다른
                          물질과 같은 문자열이 되고, 첫 100행에도 8쌍이 있다
                          (2026-09-06 실측). 그래서 ① 두 줄까지 보여 주고
                          (`line-clamp-2`: 한 줄 폭이 좁아도 실효 글자 수가
                          두 배가 된다) ② 잘린 나머지는 title 로 읽을 수 있게
                          한다(FeaturesTable 선례). 분자식 줄도 함께 봐야
                          구별되는 경우가 있어 그대로 둔다. */}
                      <span
                        className="line-clamp-2 min-w-0 flex-1 break-all"
                        title={item.name || undefined}
                      >
                        {item.name || "(이름 없음)"}
                      </span>
                      {/* 스펙트럼 보유 개수 — null/undefined(구 sidecar)는
                          아무 것도 보여주지 않는다 (없는 사실을 지어내지
                          않는다). */}
                      {item.spectrum_count != null && (
                        <span
                          className={`shrink-0 rounded px-1 text-[10px] ${
                            isSelected
                              ? "bg-indigo-500 text-indigo-50"
                              : "bg-gray-100 text-gray-600"
                          }`}
                        >
                          {item.spectrum_count > 0
                            ? `스펙트럼 ${item.spectrum_count}`
                            : "스펙트럼 없음"}
                        </span>
                      )}
                    </span>
                    {item.formula && (
                      <span
                        className={`block truncate font-mono text-xs ${
                          isSelected ? "text-indigo-100" : "text-gray-500"
                        }`}
                        title={item.formula}
                      >
                        {item.formula}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          )}

          {/* 목록의 **끝 경계**. 100건에서 멈추는 것이 잘린 게 아니라 페이지
              경계임을 말한다 — 종전에는 목록이 그냥 뚝 끊겼고, 그 자리가
              스크롤 상자의 모서리였는지 자료의 끝이었는지 알 수 없었다.
              조작 버튼은 위 고정 줄에 이미 있으므로 여기에 다시 두지 않는다
              (같은 이름의 버튼이 둘이 되면 무엇을 누르는지도 흐려진다). */}
          {!isContains && items.length > 0 && (
            <p className="mt-2 rounded border border-dashed border-gray-300 bg-gray-50 px-3 py-2 text-[11px] text-gray-600">
              {/* 전체 종수는 위 고정 줄이 이미 말한다 — 여기서 되풀이하면
                  한 화면에 같은 문장이 두 번 뜬다. 이 줄의 일은 "여기가 페이지
                  경계다"를 말하는 것뿐이다. */}
              이 페이지 끝 — {items.length}건
              {data?.start_position != null && (
                <>
                  {" · "}
                  {data.start_position.toLocaleString("ko-KR")}–
                  {(data.start_position + items.length - 1).toLocaleString(
                    "ko-KR",
                  )}
                  번째
                </>
              )}
              {data?.has_more
                ? " · 다음 페이지가 있습니다 (위 ‘다음 →’)"
                : " · 사전순 마지막입니다"}
            </p>
          )}
        </div>

        {/* 우: 상세 (참조물질 DB 탭과 같은 패널) */}
        {/* 목록이 페이지와 함께 흐르므로 상세 패널은 따라와야 한다 —
            5,000px 를 내려가도 고른 물질이 계속 보이게 고정한다. 좁은 화면
            (상하 적층)에서는 고정하지 않는다. */}
        <div
          className={`col-span-12 lg:sticky ${STICKY_TOP_DETAIL} lg:self-start ${
            activeHasSpectra ? "lg:col-span-8" : "lg:col-span-6"
          }`}
        >
          {/* 스펙트럼 상태·복수본 선택 — 상세 패널 자체는 항상 같은 구조를
              유지하고(레이아웃이 크게 흔들리지 않게), 로딩·오류·선택 상태만
              이 줄에서 말한다 (T6-3, 2026-08-27). */}
          {spectraEligible && (
            // <output> 은 role="status" 를 암묵적으로 갖는 의미 요소다
            // (biome useSemanticElements 권고) — div+role 대신 이걸 쓰고,
            // aria-live 는 명시로 유지한다. inline 기본값이라 block 을
            // 강제한다(margin-bottom 은 inline 박스엔 안 먹는다).
            <output
              className="mb-2 block min-h-[1.25rem] text-xs"
              aria-live="polite"
            >
              {isSpectraLoading ? (
                <p className="text-gray-500">스펙트럼 불러오는 중…</p>
              ) : isSpectra404 ? (
                <p className="text-gray-500">
                  이 물질의 스펙트럼은 라이브러리에 없습니다.
                </p>
              ) : isSpectraError ? (
                <div className="flex items-center gap-2 text-amber-700">
                  <span>스펙트럼을 불러오지 못했습니다.</span>
                  <button
                    type="button"
                    onClick={() => refetchSpectra()}
                    className="rounded border border-amber-300 bg-white px-2 py-0.5 text-[11px] hover:bg-amber-50"
                  >
                    다시 시도
                  </button>
                </div>
              ) : (
                <div className="flex flex-wrap items-center gap-2 text-gray-600">
                  {/* 단일본이어도 같은 형식으로 말한다("1/1") — 버튼만
                      숨긴다(적대적 리뷰 확증, 2026-08-27). */}
                  {spectra.length > 0 && (
                    <>
                      {spectra.length > 1 && (
                        <button
                          type="button"
                          onClick={() =>
                            setSpectrumIndex((i) => Math.max(0, i - 1))
                          }
                          disabled={spectrumIndex === 0}
                          aria-label="이전 스펙트럼"
                          className="rounded border border-gray-300 bg-white px-1.5 py-0.5 hover:bg-gray-50 disabled:opacity-40"
                        >
                          ‹
                        </button>
                      )}
                      <span>
                        스펙트럼 {spectrumIndex + 1}/{spectra.length}
                        {currentSpectrum && ` · ${currentSpectrum.library}`}
                        {currentSpectrum &&
                          ` · 피크 ${currentSpectrum.n_peaks}개`}
                      </span>
                      {spectra.length > 1 && (
                        <button
                          type="button"
                          onClick={() =>
                            setSpectrumIndex((i) =>
                              Math.min(spectra.length - 1, i + 1),
                            )
                          }
                          disabled={spectrumIndex === spectra.length - 1}
                          aria-label="다음 스펙트럼"
                          className="rounded border border-gray-300 bg-white px-1.5 py-0.5 hover:bg-gray-50 disabled:opacity-40"
                        >
                          ›
                        </button>
                      )}
                    </>
                  )}
                  {/* 하드코딩하지 않는다 — 서버가 준 실제 개수를 그대로
                      말한다(적대적 리뷰 확증, 2026-08-27: 종전 "상위 20개"는
                      limit 기본값을 그대로 박아 둔 것이라 limit 이 바뀌면
                      거짓말이 된다). */}
                  {spectraResp?.truncated && (
                    <span className="text-amber-700">
                      상위 {spectra.length}개만 표시 — 더 있음
                    </span>
                  )}
                </div>
              )}
            </output>
          )}
          <CompoundDetailPane
            dataset="standard-library"
            spectrumPending={spectrumPending}
            compound={
              selected
                ? {
                    name: selected.name || "(이름 없음)",
                    formula: selected.formula,
                    smiles: selected.smiles,
                    smilesSource: selected.smiles_source,
                    stereoState: selected.stereo_state,
                    peaks: currentSpectrum?.peaks,
                    spectrumSource: spectrumSourceLabel,
                  }
                : null
            }
          />
        </div>
      </div>
    </div>
  );
}
